import { useCallback, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import {
  RejectDocumentReason,
  useApproveVerificationPhotoMutation,
  useDeleteVerificationPhotoMutation,
  useRejectVerificationPhotoMutation,
} from 'entities/VerificationPhoto';
import { useGlobalConfirmModal } from 'app/globalConfirmModal';

interface HookArgs {
  verificationPhotoId: string;
  onSuccess?: () => void;
}

interface HookApi {
  isLoading: boolean;
  isRejectModalOpen: boolean;
  openRejectModal: () => void;
  closeRejectModal: () => void;
  handleApprove: () => void;
  handleReject: ({ rejectReason, note }: { rejectReason: RejectDocumentReason; note?: string }) => void;
  handleDelete: () => void;
}

export const useManageVerificationPhoto = ({ verificationPhotoId, onSuccess }: HookArgs): HookApi => {
  const { t } = useAppTranslation('common');

  const [rejectPhoto, { isLoading: isRejectLoading }] = useRejectVerificationPhotoMutation();
  const [approvePhoto, { isLoading: isApproveLoading }] = useApproveVerificationPhotoMutation();
  const [deleteVerificationPhoto, { isLoading: isDeleteLoading }] = useDeleteVerificationPhotoMutation();

  const { openModal, closeModal } = useGlobalConfirmModal();

  const [isRejectModalOpen, setRejectModalOpen] = useState(false);

  const openRejectModal = useCallback((): void => {
    setRejectModalOpen(true);
  }, []);

  const closeRejectModal = useCallback((): void => {
    setRejectModalOpen(false);
  }, []);

  const handleApprove = useCallback(async () => {
    try {
      await approvePhoto(verificationPhotoId).unwrap();

      showNotification('info', t('Success', { ns: 'common' }), t('Document approved successfully'));
      onSuccess?.();
    } catch (err) {
      console.error(err);
      showNotification('error', t('Error', { ns: 'common' }), t('Error while approving document'));
    } finally {
      closeModal();
    }
  }, [approvePhoto, verificationPhotoId, t, onSuccess, closeModal]);

  const handleReject = useCallback(
    async ({ rejectReason, note }: { rejectReason: RejectDocumentReason; note?: string }) => {
      try {
        await rejectPhoto({
          verificationPhotoId,
          note,
          rejectReason,
        }).unwrap();

        showNotification('info', t('Success', { ns: 'common' }), t('Document rejected successfully'));
        onSuccess?.();
      } catch (err) {
        console.error(err);
        showNotification('error', t('Error', { ns: 'common' }), t('Error while rejecting document'));
      } finally {
        closeRejectModal();
      }
    },
    [rejectPhoto, verificationPhotoId, t, onSuccess, closeRejectModal],
  );

  const handleDelete = useCallback(async (): Promise<void> => {
    try {
      await deleteVerificationPhoto(verificationPhotoId).unwrap();
      showNotification('info', t('Success', { ns: 'common' }), t('Document deleted successfully'));

      onSuccess?.();
    } catch (error) {
      showNotification('error', t('Error', { ns: 'common' }), t('Error when deleting document'));
      console.log('error', error);
    } finally {
      closeModal();
    }
  }, [deleteVerificationPhoto, verificationPhotoId, t, onSuccess, closeModal]);

  const openDocumentDeleteConfirmModal = useCallback((): void => {
    openModal({
      title: t('Delete document'),
      description: t('Are you sure you want to delete this document?'),
      onCancel: closeModal,
      onOk: handleDelete,
      zIndex: 4000,
    });
  }, [closeModal, handleDelete, openModal, t]);

  const openDocumentUpdateConfirmModal = useCallback((): void => {
    openModal({
      title: t('Document approvement'),
      description: t('Are you sure you want to approve document?'),
      onCancel: closeModal,
      onOk: handleApprove,
      zIndex: 4000,
    });
  }, [closeModal, handleApprove, openModal, t]);

  return {
    isLoading: isDeleteLoading || isRejectLoading || isApproveLoading,
    isRejectModalOpen,
    openRejectModal,
    closeRejectModal,
    handleApprove: openDocumentUpdateConfirmModal,
    handleReject,
    handleDelete: openDocumentDeleteConfirmModal,
  };
};
